<template>
  <a-drawer
    :visible="visible"
    :title="`Invoice #${invoiceData && invoiceData.id ? invoiceData.id : ''} Details`"
    placement="right"
    width="600px"
    @close="handleClose"
    :closable="true"
  >
    <div v-if="invoiceData" style="padding: 0 16px">
      <!-- Invoice Overview Section -->
      <div style="margin-bottom: 32px">
        <h3 style="margin-bottom: 16px; font-size: 16px; font-weight: 600; color: #05004E">
          Invoice Overview
        </h3>
        <div style="background: #f9fafb; padding: 20px; border-radius: 8px">
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 16px">
            <div>
              <p style="margin: 0 0 4px 0; font-size: 12px; color: #6b7280; font-weight: 500">
                Invoice ID
              </p>
              <p style="margin: 0; font-size: 14px; font-weight: 600; color: #05004E">
                {{ invoiceData.id }}
              </p>
            </div>
            <div>
              <p style="margin: 0 0 4px 0; font-size: 12px; color: #6b7280; font-weight: 500">
                Order ID
              </p>
              <p style="margin: 0; font-size: 14px; font-weight: 600; color: #05004E">
                {{ invoiceData.orderId }}
              </p>
            </div>
            <div>
              <p style="margin: 0 0 4px 0; font-size: 12px; color: #6b7280; font-weight: 500">
                Invoice Date
              </p>
              <p style="margin: 0; font-size: 14px; font-weight: 600; color: #05004E">
                {{ formatDate(invoiceData.createdAt) }}
              </p>
            </div>
            <div>
              <p style="margin: 0 0 4px 0; font-size: 12px; color: #6b7280; font-weight: 500">
                Due Date
              </p>
              <p style="margin: 0; font-size: 14px; font-weight: 600; color: #05004E">
                {{ formatDate(invoiceData.dueDate) }}
              </p>
            </div>
            <div>
              <p style="margin: 0 0 4px 0; font-size: 12px; color: #6b7280; font-weight: 500">
                Invoice Age
              </p>
              <p style="margin: 0; font-size: 14px; font-weight: 600; color: #05004E">
                {{ invoiceData.aging }} days
              </p>
            </div>
            <div>
              <p style="margin: 0 0 4px 0; font-size: 12px; color: #6b7280; font-weight: 500">
                Fulfillment Type
              </p>
              <p style="margin: 0; font-size: 14px; font-weight: 600; color: #05004E">
                {{ invoiceData.fulfilmentType }}
              </p>
            </div>
            <div>
              <p style="margin: 0 0 4px 0; font-size: 12px; color: #6b7280; font-weight: 500">
                Total Order Value
              </p>
              <p style="margin: 0; font-size: 14px; font-weight: 600; color: #05004E">
                {{ formatAmount(invoiceData.discountedValue) }}
              </p>
            </div>
            <div>
              <p style="margin: 0 0 4px 0; font-size: 12px; color: #6b7280; font-weight: 500">
                Factored Value
              </p>
              <p style="margin: 0; font-size: 14px; font-weight: 600; color: #26a246">
                {{ formatAmount(invoiceData.amountPayable) }}
              </p>
            </div>
            <div>
              <p style="margin: 0 0 4px 0; font-size: 12px; color: #6b7280; font-weight: 500">
                Payment Terms
              </p>
              <p style="margin: 0; font-size: 14px; font-weight: 600; color: #05004E">
                {{ invoiceData.customerPaymentTerms }} days
              </p>
            </div>
            <div>
              <p style="margin: 0 0 4px 0; font-size: 12px; color: #6b7280; font-weight: 500">
                Factoring Status
              </p>
              <StatusTag :status="invoiceData.factoringstatus" />
            </div>
          </div>
        </div>
      </div>

      <!-- Parties Information Section -->
      <div style="margin-bottom: 32px">
        <h3 style="margin-bottom: 16px; font-size: 16px; font-weight: 600; color: #05004E">
          Parties Information
        </h3>
        <div style="background: #f9fafb; padding: 20px; border-radius: 8px">
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 16px">
            <div>
              <p style="margin: 0 0 4px 0; font-size: 12px; color: #6b7280; font-weight: 500">
                Buyer Name
              </p>
              <p style="margin: 0; font-size: 14px; font-weight: 600; color: #05004E">
                {{ invoiceData.customerBusinessName }}
              </p>
            </div>
            <div>
              <p style="margin: 0 0 4px 0; font-size: 12px; color: #6b7280; font-weight: 500">
                Seller Name
              </p>
              <p style="margin: 0; font-size: 14px; font-weight: 600; color: #05004E">
                {{ invoiceData.sellerName }}
              </p>
            </div>
            <div>
              <p style="margin: 0 0 4px 0; font-size: 12px; color: #6b7280; font-weight: 500">
                Buyer Phone
              </p>
              <p style="margin: 0; font-size: 14px; font-weight: 600; color: #05004E">
                {{ invoiceData.customerPhoneNumber }}
              </p>
            </div>
          </div>
        </div>
      </div>


      <!-- Disbursement Section -->
      <div style="margin-bottom: 32px">
        <h3 style="margin-bottom: 16px; font-size: 16px; font-weight: 600; color: #05004E">
          Disbursement Information
        </h3>
        <div style="background: #f9fafb; padding: 20px; border-radius: 8px">
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 16px">
            <div>
              <p style="margin: 0 0 4px 0; font-size: 12px; color: #6b7280; font-weight: 500">
                Amount Payable
              </p>
              <p style="margin: 0; font-size: 14px; font-weight: 600; color: #05004E">
                {{ formatAmount(invoiceData.amountPayable) }}
              </p>
            </div>
            <div>
              <p style="margin: 0 0 4px 0; font-size: 12px; color: #6b7280; font-weight: 500">
                Amount Disbursed
              </p>
              <p style="margin: 0; font-size: 14px; font-weight: 600; color: #26a246">
                {{ formatAmount(invoiceData.totalDisbursed) }}
              </p>
            </div>
            <div>
              <p style="margin: 0 0 4px 0; font-size: 12px; color: #6b7280; font-weight: 500">
                Outstanding
              </p>
              <p style="margin: 0; font-size: 14px; font-weight: 600; color: #ef4444">
                {{ formatAmount((invoiceData.amountPayable || 0) - (invoiceData.totalDisbursed || 0)) }}
              </p>
            </div>
            <div>
              <p style="margin: 0 0 4px 0; font-size: 12px; color: #6b7280; font-weight: 500">
                Disbursement Status
              </p>
              <StatusTag :status="invoiceData.disbursementStatus" />
            </div>
          </div>
        </div>
      </div>

      <!-- Repayment Information Section -->
      <div style="margin-bottom: 32px">
        <h3 style="margin-bottom: 16px; font-size: 16px; font-weight: 600; color: #05004E">
          Repayment Information
        </h3>
        <div style="background: #f9fafb; padding: 20px; border-radius: 8px">
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 16px">
            <div>
              <p style="margin: 0 0 4px 0; font-size: 12px; color: #6b7280; font-weight: 500">
                Amount to Repay
              </p>
              <p style="margin: 0; font-size: 14px; font-weight: 600; color: #05004E">
                {{ formatAmount(invoiceData.discountedValue) }}
              </p>
            </div>
            <div>
              <p style="margin: 0 0 4px 0; font-size: 12px; color: #6b7280; font-weight: 500">
                Amount Repaid
              </p>
              <p style="margin: 0; font-size: 14px; font-weight: 600; color: #26a246">
                {{ formatAmount(invoiceData.totalRepaid) }}
              </p>
            </div>
            <div>
              <p style="margin: 0 0 4px 0; font-size: 12px; color: #6b7280; font-weight: 500">
                Outstanding
              </p>
              <p style="margin: 0; font-size: 14px; font-weight: 600; color: #ef4444">
                {{ formatAmount(invoiceData.repaymentOutstanding) }}
              </p>
            </div>
            <div>
              <p style="margin: 0 0 4px 0; font-size: 12px; color: #6b7280; font-weight: 500">
                Repayment Status
              </p>
              <StatusTag :status="invoiceData.repaymentStatus" />
            </div>
          </div>
        </div>
      </div>

    </div>

    <!-- Loading State -->
    <div v-else-if="loading" style="display: flex; justify-content: center; align-items: center; height: 200px">
      <a-spin size="large" />
    </div>

    <!-- Error State -->
    <div v-else style="display: flex; justify-content: center; align-items: center; height: 200px; color: #6b7280">
      <div style="text-align: center">
        <a-icon type="exclamation-circle" style="font-size: 48px; margin-bottom: 16px; color: #d1d5db" />
        <p>No invoice data available</p>
      </div>
    </div>

    <!-- Footer -->
    <div style="position: absolute; bottom: 0; left: 0; right: 0; padding: 16px; background: #fff; border-top: 1px solid #e6e6e6">
      <div style="display: flex; justify-content: flex-end; gap: 12px">
        <a-button @click="handleClose" style="border-radius: 6px; background: #26a246; border-color: #26a246; color: #fff">
          Close
        </a-button>
      </div>
    </div>
  </a-drawer>
</template>

<script>
import StatusTag from "../shared-components/StatusTag.vue";
import { formatAmount, formatDate } from "@/utils";

export default {
  name: "InvoiceDetailsDrawer",
  components: {
    StatusTag,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    invoiceData: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      loading: false,
    };
  },
  methods: {
    formatAmount,
    formatDate,
    handleClose() {
      this.$emit("close");
    },
  },
};
</script>

<style scoped>
.ant-drawer-body {
  padding: 0;
  position: relative;
  padding-bottom: 80px;
}
</style>
